import { format, formatDistanceToNow, isToday, isYesterday, isTomorrow } from "date-fns";
import { fr, enUS } from "date-fns/locale";

const localeMap = { fr, en: enUS } as const;

export function formatFcfa(amount: number, opts: { compact?: boolean } = {}): string {
  if (opts.compact && amount >= 1_000_000) {
    return `${(amount / 1_000_000).toFixed(1).replace(/\.0$/, "")}M FCFA`;
  }
  if (opts.compact && amount >= 10_000) {
    return `${(amount / 1_000).toFixed(1).replace(/\.0$/, "")}k FCFA`;
  }
  return new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 })
    .format(Math.round(amount))
    .concat(" FCFA");
}

export function parseFcfa(input: string): number {
  return Number(input.replace(/[^\d]/g, ""));
}

export function formatDate(date: Date | string, lang: "fr" | "en" = "fr"): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return format(d, "d MMM yyyy", { locale: localeMap[lang] });
}

export function formatTime(date: Date | string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return format(d, "HH:mm");
}

export function formatRelative(date: Date | string, lang: "fr" | "en" = "fr"): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return formatDistanceToNow(d, { addSuffix: true, locale: localeMap[lang] });
}

export function formatMatchDay(date: Date | string, lang: "fr" | "en" = "fr"): string {
  const d = typeof date === "string" ? new Date(date) : date;
  if (isToday(d)) return lang === "fr" ? "Aujourd'hui" : "Today";
  if (isYesterday(d)) return lang === "fr" ? "Hier" : "Yesterday";
  if (isTomorrow(d)) return lang === "fr" ? "Demain" : "Tomorrow";
  return format(d, "EEE d MMM", { locale: localeMap[lang] });
}

/**
 * Formate un numéro international en groupes de 2-3 chiffres.
 * Reconnaît le préfixe pays via la liste COUNTRIES si E.164.
 */
export function formatPhone(phone: string): string {
  if (!phone) return "";
  const raw = phone.trim();
  if (!raw.startsWith("+")) {
    return raw.replace(/(\d{2})(?=\d)/g, "$1 ").trim();
  }
  // E.164 : on cherche le code pays
  const digits = raw.slice(1).replace(/\D/g, "");
  for (let len = 4; len >= 1; len--) {
    const prefix = digits.slice(0, len);
    // import à plat pour éviter cycle
    const country = COUNTRY_DIAL_INDEX.get(prefix);
    if (country) {
      const local = digits.slice(len);
      return `+${prefix} ${local.replace(/(\d{2,3})(?=\d)/g, "$1 ").trim()}`;
    }
  }
  return raw;
}

// Index dial → présence (chargé paresseusement pour éviter le cycle d'import)
const COUNTRY_DIAL_INDEX: Map<string, boolean> = new Map();
(async () => {
  const { COUNTRIES } = await import("./countries");
  for (const c of COUNTRIES) COUNTRY_DIAL_INDEX.set(c.dial, true);
})();

export function formatRank(rank: number): string {
  if (rank === 1) return "🥇";
  if (rank === 2) return "🥈";
  if (rank === 3) return "🥉";
  return `#${rank}`;
}
